import { isSpeechSupported } from "../core/SpeechController";

/**
 * Anivaze v4 — voiceover (clean, adult, non-childish).
 *
 * Speaks the Director's narration via Web Speech during playback. Two hard rules
 * vs the legacy voice: (1) sanitize the text so it NEVER reads scaffolding like
 * "Here's the narration text:", and (2) neutral adult delivery (rate ~1, pitch 1)
 * — not the kid pitch. Exportable/premium TTS is a later phase (PLAN §10); Web
 * Speech can't be captured into the export file.
 */

export { isSpeechSupported };

/**
 * Strip model scaffolding and wrapping quotes so the voice/captions read only the
 * actual line. Kills the "Here's the narration text for this step:" bug at the
 * text level, wherever the text came from.
 */
export function sanitizeNarration(text: string): string {
  let t = (text ?? "").replace(/\s+/g, " ").trim();
  // Leading meta preambles, optionally repeated.
  for (let i = 0; i < 2; i++) {
    t = t.replace(
      /^\s*(?:here'?s|here is|this is|below is)?\s*(?:the\s+)?(?:narration|voice[- ]?over|script|text|caption|dialogue)(?:\s+(?:narration|voice[- ]?over|script|text|caption|dialogue))*(?:\s+(?:for|of)\s+(?:this|the)\s+(?:step|scene|beat|slide))?\s*[:\-–]\s*/i,
      "",
    );
    t = t.replace(/^\s*(?:step|scene|beat)\s*\d+\s*[:\-–]\s*/i, "");
  }
  // Wrapping quotes.
  t = t.replace(/^["'“”‘’]+/, "").replace(/["'“”‘’]+$/, "").trim();
  return t;
}

let cached: SpeechSynthesisVoice | null = null;
function pickVoice(): SpeechSynthesisVoice | null {
  if (!isSpeechSupported()) return null;
  const voices = window.speechSynthesis.getVoices();
  if (!voices.length) return null;
  const en = voices.filter((v) => /^en/i.test(v.lang));
  const best =
    en.find((v) => /natural|neural|online/i.test(v.name)) ||
    en.find((v) => /aria|jenny|guy|google us english|samantha|libby|sonia/i.test(v.name)) ||
    en.find((v) => /google/i.test(v.name)) ||
    en[0] ||
    voices[0];
  return best ?? null;
}

if (isSpeechSupported()) {
  cached = pickVoice();
  window.speechSynthesis.onvoiceschanged = () => {
    cached = pickVoice();
  };
}

/** Speak one narration line (cancels anything already speaking). */
export function speakV4(text: string): void {
  if (!isSpeechSupported()) return;
  const clean = sanitizeNarration(text);
  if (!clean) return;
  const ss = window.speechSynthesis;
  ss.cancel();
  const u = new SpeechSynthesisUtterance(clean);
  if (!cached) cached = pickVoice();
  if (cached) u.voice = cached;
  u.rate = 1.0;
  u.pitch = 1.0;
  u.volume = 1;
  ss.speak(u);
}

export function cancelV4(): void {
  if (isSpeechSupported()) window.speechSynthesis.cancel();
}
